import { RefObject } from '@fullcalendar/core/preact';
import { AddCircleOutline } from '@mui/icons-material';
import { AlertColor, Box, Button, Divider, Grid, Modal } from '@mui/material';
import strings from 'common/Translation/Translate';
import Form, { FormDataModel } from 'components/Form';
import Notification from 'components/Notification';
import Section from 'components/Section/Section';
import { FC, MouseEvent, useCallback, useMemo, useRef, useState } from 'react';
import { removeErrorFieldsFromValues } from 'utils/validators';
import { CompetitionHotelForm } from './CompetitionsForm';
import CompetitionsTable from './CompetitionsTable';

interface CompetitionHotelProps {
  competitions: Array<any>;
  setCompetitions: (value: Array<any>) => void;
}

interface MessageState {
  display: boolean;
  severity: AlertColor | null;
  message: string;
}

const CompetitionHotel: FC<CompetitionHotelProps> = ({ competitions, setCompetitions }: any) => {
  const formRef: RefObject<Form | null | undefined> = useRef();

  const [hasError, setHasError] = useState<boolean>(false);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [pageResponse, setPageResponse] = useState<any>([]);
  const [selectedRowId, setSelectedRowId] = useState<any>(null);
  const [message, setMessage] = useState<MessageState>({
    display: false,
    severity: null,
    message: '',
  });

  const handleSave = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const { getFormData } = formRef.current as {
      getFormData: () => { formData: FormDataModel; isFormValid: boolean };
    };
    const { formData, isFormValid } = getFormData();
    let data = removeErrorFieldsFromValues(formData);

    setHasError(false);
    if (isFormValid) {
      const duplicate = competitions.some(
        (item: any, index: number) => item.hotelName === data.hotelName && index !== selectedRowId,
      );

      if (duplicate) {
        setMessage({
          display: true,
          severity: 'error',
          message: strings.sales_management_branch_duplicate_competitions,
        });
      } else {
        const updatedData = [...competitions];
        if (selectedRowId !== null) {
          updatedData[selectedRowId] = { ...updatedData[selectedRowId], ...data };
        } else {
          updatedData.push(data);
        }

        setCompetitions(updatedData);
        setOpenForm(false);
        setPageResponse({});
        setSelectedRowId(null);
      }
    } else {
      setHasError(true);
    }
  };

  const handleEdit = useCallback(
    (idx: number) => {
      const editRow = JSON.parse(JSON.stringify(competitions));
      const editValue = editRow.find((item: any, i: any) => i === idx);

      setSelectedRowId(idx);
      setPageResponse({ ...editValue });
      setOpenForm(true);
    },
    [competitions],
  );

  const handleDelete = useCallback(
    (idx: number) => {
      const deleteRowMapping = JSON.parse(JSON.stringify(competitions));
      const deleteValue = deleteRowMapping.filter((item: any, i: any) => i !== idx);
      setCompetitions(deleteValue);
    },
    [competitions],
  );

  const micePotentialModel = useMemo(() => {
    return (
      <Modal
        open={openForm}
        onClose={() => setOpenForm(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{
          border: 0,
        }}>
        <Box
          sx={{
            position: 'absolute' as 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            p: 3,
            width: '65vw',
            minHeight: '50vh',
            display: 'flex',
            flexDirection: 'column',
          }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}>
            <Box component="h2" sx={{ m: 0, fontSize: '1.5rem' }}>
              {strings.sales_management_branch_add_competitions_heading}
            </Box>
            <Button onClick={() => setOpenForm(false)} size="small" sx={{ minWidth: '32px', color: 'grey.600' }}>
              X
            </Button>
          </Box>
          <Divider />
          <Grid container spacing={2} sx={{ my: 2 }}>
            <Form
              hasError={hasError}
              ref={formRef as RefObject<Form>}
              model={CompetitionHotelForm(strings)}
              values={pageResponse}
            />
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              data-testid="save"
              sx={{ color: '#ffffff', minWidth: '120px' }}
              onClick={(e: any) => handleSave(e)}>
              {strings.common_button_save}
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }, [openForm]);

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={() => setMessage({ ...message, display: false })}
        />
      )}

      <Box width={'100%'} sx={{ px: 2 }}>
        <Button
          data-testid="openForm_btn"
          onClick={() => setOpenForm(true)}
          variant="text"
          sx={{
            px: 0,
            '.MuiButton-endIcon': { marginLeft: '8px' },
            minHeight: '36px',
            textTransform: 'capitalize',
            fontSize: 16,
            fontWeight: 700,
          }}
          endIcon={<AddCircleOutline />}>
          {strings.sales_management_branch_add_competitions_heading}
        </Button>
        {!competitions.length && <Section />}

        {competitions.length > 0 &&
          <>
            <CompetitionsTable competitions={competitions} onEdit={handleEdit} onDelete={handleDelete} />
            <Section />
          </>
        }
      </Box>
      {openForm && micePotentialModel}
    </>
  );
};
export default CompetitionHotel;
